/**
 * Action review screen.
 *
 * The reviewer sees the exact before/after, the risk, a live preview against
 * their site, the approval history, and — after execution — the independent
 * verification result. Approve, edit-then-approve, and reject are all here.
 *
 * The decision sits in the pinned rail rather than at the bottom of the column:
 * reviewing means reading the diff and the preview and *then* deciding, and a
 * decision you have to scroll back up to reach is one made with the evidence
 * off screen.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { actionApi } from '@/api/endpoints';
import { useAuth } from '@/app/auth';
import { Permission } from '@/app/permissions';
import {
  AlertIcon,
  ArrowRightIcon,
  BoltIcon,
  CheckCircleIcon,
  ClockIcon,
  InfoIcon,
  ShieldIcon,
} from '@/components/icons';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  DataRow,
  DetailLayout,
  DetailMain,
  DetailRail,
  DiffView,
  ErrorState,
  LoadingState,
  MetaBar,
  PageHeader,
  RiskBadge,
  StatusBadge,
} from '@/components/ui';
import { formatDateTime, inputClass } from '@/utils/format';
import type { ActionDetail, VerificationDetail } from '@/types/api';

export function ActionDetailPage() {
  const { actionId = '' } = useParams();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [editedValue, setEditedValue] = useState('');
  const [comment, setComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const query = useQuery({
    queryKey: ['action', actionId],
    queryFn: () => actionApi.get(actionId),
    refetchInterval: (q) => {
      const data = q.state.data as ActionDetail | undefined;
      // Executing/verifying states resolve on their own; poll until they do.
      return data && ['executing', 'verifying', 'approved'].includes(data.status) ? 4000 : false;
    },
  });

  const approvalsQuery = useQuery({
    queryKey: ['action', actionId, 'approvals'],
    queryFn: () => actionApi.approvals(actionId),
  });

  const executionsQuery = useQuery({
    queryKey: ['action', actionId, 'executions'],
    queryFn: () => actionApi.executions(actionId),
  });

  const verificationsQuery = useQuery({
    queryKey: ['action', actionId, 'verifications'],
    queryFn: () => actionApi.verifications(actionId),
    enabled: Boolean(query.data && ['verifying', 'completed', 'failed'].includes(query.data.status)),
  });

  const previewQuery = useQuery({
    queryKey: ['action', actionId, 'preview'],
    queryFn: () => actionApi.preview(actionId),
    enabled: Boolean(query.data && query.data.status === 'pending_approval'),
    retry: false,
  });

  async function invalidate() {
    await queryClient.invalidateQueries({ queryKey: ['action', actionId] });
    await queryClient.invalidateQueries({ queryKey: ['actions'] });
  }

  const approveMutation = useMutation({
    mutationFn: () =>
      actionApi.approve(actionId, {
        comment: comment || undefined,
        edited_after_state: editing && editedValue ? { after: editedValue } : undefined,
      }),
    onSuccess: invalidate,
  });

  const rejectMutation = useMutation({
    mutationFn: () => actionApi.reject(actionId, { reason: rejectReason || undefined }),
    onSuccess: invalidate,
  });

  const executeMutation = useMutation({
    mutationFn: () => actionApi.execute(actionId),
    onSuccess: invalidate,
  });

  const rollbackMutation = useMutation({
    mutationFn: () => actionApi.rollback(actionId),
    onSuccess: invalidate,
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) {
    return (
      <ErrorState message={(query.error as Error).message} onRetry={() => void query.refetch()} />
    );
  }

  const action = query.data!;
  const before = String(action.before_state.before ?? '');
  const after = String(action.after_state.after ?? '');
  const mutationError = [
    approveMutation.error,
    rejectMutation.error,
    executeMutation.error,
    rollbackMutation.error,
  ].find(Boolean);

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/actions" className="inline-flex items-center gap-1 hover:text-ink">
            <ArrowRightIcon className="h-3.5 w-3.5 rotate-180" />
            Action center
          </Link>
        }
        title={action.title}
        description={action.reason ?? undefined}
      />

      <MetaBar>
        <StatusBadge status={action.status} />
        <RiskBadge risk={action.risk} />
        <Badge tone="neutral">{action.action_type.replace(/_/g, ' ').toLowerCase()}</Badge>
        <Badge tone="neutral">confidence {Math.round(action.confidence * 100)}%</Badge>
        {action.is_ai_generated ? <Badge tone="brand">AI generated</Badge> : null}
        {action.executor_provider ? (
          <Badge tone="neutral">via {action.executor_provider}</Badge>
        ) : null}
      </MetaBar>

      <DetailLayout>
        <DetailMain>
          {mutationError ? (
            <Callout tone="danger" icon={<AlertIcon className="h-5 w-5" />}>
              {mutationError instanceof ApiError
                ? mutationError.message
                : 'The request could not be completed.'}
            </Callout>
          ) : null}

          {action.failure_reason ? (
            <Callout
              tone="danger"
              title="This action did not complete"
              icon={<AlertIcon className="h-5 w-5" />}
            >
              {action.failure_reason}
            </Callout>
          ) : null}

          <Card>
            <CardHeader
              title="The change"
              icon={<BoltIcon className="h-4 w-4" />}
              description="Exactly what will be written to your website."
              action={
                can(Permission.ACTION_EDIT) && action.status === 'pending_approval' ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditing((current) => !current);
                      setEditedValue(after);
                    }}
                  >
                    {editing ? 'Cancel edit' : 'Edit value'}
                  </Button>
                ) : null
              }
            />

            {action.target_url ? (
              <a
                href={action.target_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mb-4 block truncate rounded-lg bg-surface-muted px-3 py-2 font-mono text-xs text-brand hover:underline"
                title={action.target_url}
              >
                {action.target_url}
              </a>
            ) : null}

            {editing ? (
              <div className="space-y-3">
                <DiffView
                  before={before}
                  after={<em className="text-ink-subtle">editing below</em>}
                />
                <div>
                  <label htmlFor="edited" className="block text-sm font-medium text-ink">
                    Your value
                  </label>
                  <textarea
                    id="edited"
                    rows={3}
                    value={editedValue}
                    onChange={(event) => setEditedValue(event.target.value)}
                    className={`${inputClass} mt-1.5`}
                  />
                  <p className="mt-1 text-xs text-ink-subtle">
                    Your edit is validated against the same rules as a generated value, and the
                    approval record stores exactly what you approved.
                  </p>
                </div>
              </div>
            ) : (
              <DiffView before={before} after={after} />
            )}

            {action.changes.length > 0 ? (
              <ul className="mt-4 space-y-1 border-t border-border-subtle pt-4 text-xs text-ink-subtle">
                {action.changes.map((change) => (
                  <li key={change.id}>
                    <span className="font-medium text-ink-muted">{change.field}</span> on{' '}
                    <span className="font-mono">{change.resource}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </Card>

          {action.expected_impact ? (
            <Card>
              <CardHeader title="Expected impact" icon={<InfoIcon className="h-4 w-4" />} />
              <p className="text-sm leading-relaxed text-ink-muted">{action.expected_impact}</p>
            </Card>
          ) : null}

          {previewQuery.data ? (
            <Card>
              <CardHeader
                title="Live preview"
                icon={<ShieldIcon className="h-4 w-4" />}
                description="Checked against your website right now, without changing anything."
              />
              {previewQuery.data.valid ? (
                <Callout
                  tone="success"
                  title="Ready to apply"
                  icon={<CheckCircleIcon className="h-5 w-5" />}
                >
                  {previewQuery.data.summary}
                </Callout>
              ) : (
                <Callout
                  tone="danger"
                  title="This change cannot be applied yet"
                  icon={<AlertIcon className="h-5 w-5" />}
                >
                  <ul className="mt-1 list-disc space-y-0.5 pl-4">
                    {previewQuery.data.reasons.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                </Callout>
              )}
              {previewQuery.data.warnings.length > 0 ? (
                <div className="mt-3">
                  <Callout tone="warning" icon={<AlertIcon className="h-5 w-5" />}>
                    <ul className="list-disc space-y-0.5 pl-4">
                      {previewQuery.data.warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  </Callout>
                </div>
              ) : null}
              {previewQuery.data.diff ? (
                <pre className="scroll-x mt-3 rounded-lg bg-surface-muted p-3 text-xs whitespace-pre">
                  {previewQuery.data.diff}
                </pre>
              ) : null}
            </Card>
          ) : null}

          {previewQuery.isError ? (
            <Callout
              tone="warning"
              title="Preview unavailable"
              icon={<AlertIcon className="h-5 w-5" />}
            >
              {previewQuery.error instanceof ApiError
                ? previewQuery.error.message
                : 'Could not reach your website to preview this change.'}
            </Callout>
          ) : null}

          {verificationsQuery.data && verificationsQuery.data.length > 0 ? (
            <Card>
              <CardHeader
                title="Verification"
                icon={<CheckCircleIcon className="h-4 w-4" />}
                description="We re-fetched the live page to confirm the change actually landed."
              />
              <div className="space-y-5">
                {verificationsQuery.data.map((run) => (
                  <VerificationBlock key={run.id} run={run} />
                ))}
              </div>
            </Card>
          ) : null}

          {executionsQuery.data && executionsQuery.data.length > 0 ? (
            <Card>
              <CardHeader title="Execution history" icon={<ClockIcon className="h-4 w-4" />} />
              <ul className="divide-y divide-border-subtle">
                {executionsQuery.data.map((execution) => (
                  <li key={execution.id} className="flex items-start justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge status={execution.status} />
                        <span className="text-xs text-ink-subtle">
                          attempt {execution.attempt} · {execution.provider}
                        </span>
                      </div>
                      {execution.error_message ? (
                        <p className="mt-1 text-xs text-danger">{execution.error_message}</p>
                      ) : null}
                      {execution.pull_request_url ? (
                        <a
                          href={execution.pull_request_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
                        >
                          View pull request
                          <ArrowRightIcon className="h-3 w-3" />
                        </a>
                      ) : null}
                    </div>
                    <span className="shrink-0 text-xs text-ink-subtle">
                      {formatDateTime(execution.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
              {action.rollback_available && can(Permission.ACTION_ROLLBACK) ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-4"
                  loading={rollbackMutation.isPending}
                  onClick={() => rollbackMutation.mutate()}
                >
                  Roll back this change
                </Button>
              ) : null}
            </Card>
          ) : null}
        </DetailMain>

        <DetailRail>
          {action.status === 'pending_approval' && can(Permission.ACTION_APPROVE) ? (
            <Card className="border-brand-border bg-brand-subtle">
              <CardHeader
                title="Your decision"
                description="Nothing has been changed on your website yet."
              />
              <div className="space-y-3">
                <div>
                  <label htmlFor="comment" className="block text-sm font-medium text-ink">
                    Comment (optional)
                  </label>
                  <input
                    id="comment"
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    className={`${inputClass} mt-1.5`}
                    placeholder="Recorded in the approval history"
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="success"
                    loading={approveMutation.isPending}
                    onClick={() => approveMutation.mutate()}
                    icon={<CheckCircleIcon className="h-4 w-4" />}
                  >
                    {editing ? 'Approve with my edit' : 'Approve'}
                  </Button>
                  <Button variant="secondary" onClick={() => setShowReject((current) => !current)}>
                    Reject
                  </Button>
                </div>

                <p className="text-xs text-ink-subtle">
                  Approving queues this change for execution and independent verification.
                </p>

                {showReject ? (
                  <div className="border-t border-brand-border pt-3">
                    <label htmlFor="reject-reason" className="block text-sm font-medium text-ink">
                      Why are you rejecting this?
                    </label>
                    <input
                      id="reject-reason"
                      value={rejectReason}
                      onChange={(event) => setRejectReason(event.target.value)}
                      className={`${inputClass} mt-1.5`}
                      placeholder="Helps improve future recommendations"
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      className="mt-2"
                      loading={rejectMutation.isPending}
                      onClick={() => rejectMutation.mutate()}
                    >
                      Confirm rejection
                    </Button>
                  </div>
                ) : null}
              </div>
            </Card>
          ) : null}

          {action.status === 'approved' && can(Permission.ACTION_EXECUTE) ? (
            <Card className="border-success/25 bg-success-subtle">
              <CardHeader
                title="Approved — ready to apply"
                description="Applying makes this change on your website, then verifies it independently."
              />
              <Button
                variant="primary"
                loading={executeMutation.isPending}
                onClick={() => executeMutation.mutate()}
                icon={<BoltIcon className="h-4 w-4" />}
              >
                Apply this change
              </Button>
            </Card>
          ) : null}

          <Card>
            <CardHeader title="Details" icon={<InfoIcon className="h-4 w-4" />} />
            <div className="divide-y divide-border-subtle">
              <DataRow label="Status">
                <StatusBadge status={action.status} />
              </DataRow>
              <DataRow label="Risk">
                <RiskBadge risk={action.risk} />
              </DataRow>
              <DataRow label="Confidence">
                <span className="tnum text-ink">{Math.round(action.confidence * 100)}%</span>
              </DataRow>
              {action.executor_provider ? (
                <DataRow label="Executor">
                  <span className="text-ink">{action.executor_provider}</span>
                </DataRow>
              ) : null}
              <DataRow label="Rollback">
                <span className="text-ink">
                  {action.rollback_available ? 'available' : 'not available'}
                </span>
              </DataRow>
            </div>
          </Card>

          {approvalsQuery.data && approvalsQuery.data.length > 0 ? (
            <Card>
              <CardHeader
                title="Approval history"
                icon={<ClockIcon className="h-4 w-4" />}
                description="Append-only. Decisions are never rewritten."
              />
              <ul className="divide-y divide-border-subtle">
                {approvalsQuery.data.map((approval) => (
                  <li key={approval.id} className="py-2.5 first:pt-0 last:pb-0">
                    <div className="flex items-start justify-between gap-2">
                      <Badge
                        tone={
                          approval.decision === 'rejected'
                            ? 'danger'
                            : approval.decision === 'approved_with_edits'
                              ? 'info'
                              : 'success'
                        }
                      >
                        {approval.decision.replace(/_/g, ' ')}
                      </Badge>
                      <span className="shrink-0 text-[11px] text-ink-subtle">
                        {formatDateTime(approval.created_at)}
                      </span>
                    </div>
                    {approval.is_automatic ? (
                      <p className="mt-1 text-xs text-ink-subtle">
                        automatic ({approval.auto_rule})
                      </p>
                    ) : null}
                    {approval.comment ? (
                      <p className="mt-1 text-sm text-ink-muted">{approval.comment}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </DetailRail>
      </DetailLayout>
    </>
  );
}

function VerificationBlock({ run }: { run: VerificationDetail }) {
  const tone = run.status === 'passed' ? 'success' : run.status === 'failed' ? 'danger' : 'warning';
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={run.status} />
        <span className="text-xs text-ink-subtle">
          attempt {run.attempt} · {formatDateTime(run.finished_at ?? run.created_at)}
        </span>
      </div>
      {run.conclusion ? (
        <div className="mt-2">
          <Callout tone={tone}>{run.conclusion}</Callout>
        </div>
      ) : null}
      {run.checks.length > 0 ? (
        <ul className="mt-3 space-y-1.5">
          {run.checks.map((check) => (
            <li key={check.name} className="flex items-start gap-2 text-xs">
              <span className={check.passed ? 'text-success' : 'text-danger'} aria-hidden="true">
                {check.passed ? '✓' : '✗'}
              </span>
              <span className="min-w-0">
                <span className="font-medium text-ink">{check.name}</span>
                {!check.passed ? (
                  <span className="ml-1 text-ink-muted">
                    — expected <code className="font-mono">{truncate(check.expected)}</code>, found{' '}
                    <code className="font-mono">{truncate(check.observed)}</code>
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function truncate(value: string, max = 60): string {
  if (!value) return '(empty)';
  return value.length > max ? `${value.slice(0, max)}…` : value;
}
